// API Service - All backend calls
import axios from 'axios';
import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  Student,
  Course,
  CourseEnrollment,
  
  Section,
  Certificate,
} from '../types';

export interface Announcement {
  id: string;
  title: string;
  message: string;
  target: string;
  date?: string;
  author?: string;
}
 
export interface VerifyPaymentResponse {
  success: boolean;
  message: string;
  data: Payment;
}

type ApiResponse<T> = {
  success: boolean;
  data: T;
};

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://coursyflix-backend.onrender.com/api';

// Helper function to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

async function fetchWithAuth<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }

  return response.json();
}
// Authentication APIs
export const authAPI = {
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    return fetchWithAuth('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  login: async (data: LoginRequest): Promise<AuthResponse> => {
    return fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // ✅ ADD THIS HERE
  resetPassword: async (data: { email: string; newPassword: string }) => {
    const res = await fetchWithAuth<{
      success: boolean;
      message: string;
    }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return res;
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('student');
  },

  getCurrentStudent: (): Student | null => {
    const studentData = localStorage.getItem('student');
    return studentData ? JSON.parse(studentData) : null;
  },

  saveAuth: (token: string, student: Student) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('student', JSON.stringify(student));
  },
};

// Course APIs
export const courseAPI = {
  getPublishedCourses: async (): Promise<Course[]> => {
  const res = await fetchWithAuth<Course[] | { data: Course[] }>('/courses/published');
  return Array.isArray(res) ? res : res.data || [];
  },
  getCourseById: async (courseId: string): Promise<Course> => {
    return fetchWithAuth(`/courses/${courseId}`);
  },

  getCourseSections: async (courseId: string): Promise<ApiResponse<Section[]>> => {
  return fetchWithAuth(`/courses/${courseId}/sections`);
  },
};

// Enrollment APIs
export const enrollmentAPI = {
  enrollInCourse: async (courseId: string): Promise<CourseEnrollment> => {
    return fetchWithAuth('/dashboard/enrollments', {
      method: 'POST',
      body: JSON.stringify({ courseId }),
    });
  },

  // ✅ This should get enrollments from the authenticated user (no studentId param)
  getMyEnrollments: async (): Promise<CourseEnrollment[]> => {
    const res = await fetchWithAuth<{ success: boolean; data: CourseEnrollment[] }>(
      '/dashboard/enrollments'  // ✅ Just /dashboard/enrollments, no studentId
    );
    return res.data;
  },

  getStudentEnrollments: async (): Promise<CourseEnrollment[]> => {
    const res = await fetchWithAuth<{ success: boolean; data: CourseEnrollment[] }>(
      '/dashboard/enrollments'
    );
    return res.data;
  },

  getStudentEnrollmentsForPayment: async (): Promise<CourseEnrollment[]> => {
    const res = await fetchWithAuth<{ success: boolean; data: CourseEnrollment[] }>(
      '/dashboard/enrollments/pending-payment',
      { method: 'GET' }
    );
    return res.data;
  },

  updateProgress: async (
    enrollmentId: string,
    progress: number
  ): Promise<CourseEnrollment> => {
    return fetchWithAuth(`/dashboard/enrollments/${enrollmentId}/progress`, {
      method: 'PUT',
      body: JSON.stringify({ progress }),
    });
  },

  markChapterComplete: async (
    enrollmentId: string,
    chapterId: string
  ): Promise<CourseEnrollment> => {
    return fetchWithAuth(
      `/dashboard/enrollments/${enrollmentId}/chapters/${chapterId}/complete`,
      {
        method: 'POST',
      }
    );
  },
};
export const certificateAPI = {
  // =========================
  // GENERATE CERTIFICATE
  // =========================
  generateCertificate: async (
    courseId: string,
    studentName: string
  ): Promise<ApiResponse<Certificate>> => {

    // Validation
    if (!courseId || typeof courseId !== 'string') {
      throw new Error('Valid course ID is required');
    }

    if (!studentName || typeof studentName !== 'string' || studentName.trim().length === 0) {
      throw new Error('Student name is required');
    }

    if (studentName.length > 120) {
      throw new Error('Student name is too long (max 120 characters)');
    }

    // Sanitize
    const sanitizedName = studentName.trim().replace(/\s+/g, ' ');

    const res = await fetchWithAuth<ApiResponse<Certificate>>(
      `/enrollments/certificate/${courseId}`,
      {
        method: 'POST',
        body: JSON.stringify({
          studentName: sanitizedName,
        }),
      }
    );

    // Extra safety check
    if (!res?.success || !res?.data) {
      throw new Error('Invalid certificate response from server');
    }

    return res;
  },

  // =========================
  // DOWNLOAD CERTIFICATE
  // =========================
  downloadCertificate: async (certificateId: string): Promise<Blob> => {
    if (!certificateId || typeof certificateId !== 'string') {
      throw new Error('Valid certificate ID is required');
    }

    const token = getAuthToken();

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(
      `${API_BASE_URL}/enrollments/certificate/download/${certificateId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      let message = 'Download failed';

      try {
        const text = await response.text();
        if (text) message = text;
      } catch {}

      if (response.status === 404) {
        throw new Error('Certificate not found or access denied');
      }

      throw new Error(message);
    }

    return response.blob();
  },
};

export const announcementAPI = {
  getAnnouncements: async (): Promise<any> => {
    return fetchWithAuth('/announcements', {
      method: 'GET',
    });
  },
}; 

// ✅ Challenge APIs
// ✅ Challenge APIs (FIXED)
export const challengeAPI = {
  getChallenges: async (): Promise<any[]> => {
    const res = await fetchWithAuth<{ success: boolean; data: any[] }>(
      '/student/challenges'
    );
    return res.data;
  },

  getSubmissions: async (): Promise<any[]> => {
    const res = await fetchWithAuth<{ success: boolean; data: any[] }>(
      '/student/submissions'
    );
    return res.data;
  },

  submitChallenge: async (
    challengeId: string,
    content: string
  ): Promise<any> => {
    const res = await fetchWithAuth<{ success: boolean; data: any }>(
      `/student/challenges/${challengeId}/submit`,
      {
        method: 'POST',
        body: JSON.stringify({ content }),
      }
    );
    return res.data;
  },
};


export interface CourseSelection {
  courseId: string;
  months: number;
}

export interface Payment {
  id: string;
  transactionId: string;
  paymentGroupId: string;
  pesapalOrderTrackingId: string;
  studentId: string;
  courseId: string;
  amount: number;
  currency: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  paymentMethod: string;
  monthsPaid: number;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  course?: {
    id: string;
    title: string;
    name: string;
  };
}

export interface SupportedCurrency {
  code: string;
  symbol: string;
  name: string;
  flag: string;
  rateToUSD: number;
  isActive: boolean;
}

// ✅ Updated Response Types with Multi-Currency Support
export interface PaymentInitiateResponse {
  success: boolean;
  redirectUrl: string;
  paymentGroupId: string;
  totalAmount: number; // Changed from totalKES
  currency: string; // Added
  message?: string;

  // ✅ NEW FIELDS (add these)
  mode?: "REUSE" | "CREATE";
  existingPayment?: {
    id: string;
    amount: number;
    currency: string;
    createdAt: string;
  };

  note?: string;

}


export interface PaymentInitiatePayload {
  courseSelections: CourseSelection[];
  currency?: string; // Added optional currency
}

export interface PaymentSummaryResponse {
  success: boolean;
  data: any[];
  config: {
    usdPerMonth: number;
    pricePerMonth: number; // Changed from pricePerMonthKES
    currency: string; // Dynamic
    currencySymbol: string; // Added
    supportedCurrencies: SupportedCurrency[];
  };
}

export interface PaymentStatusResponse {
  success: boolean;
  data: any[];
  config: {
    usdPerMonth: number;
    pricePerMonth: number; // Changed from pricePerMonthKES
    currency: string; // Dynamic
    currencySymbol: string; // Added
    supportedCurrencies: SupportedCurrency[];
  };
}

export interface CheckAccessResponse {
  accessible: boolean;
  reason: 'ACTIVE' | 'NOT_STARTED' | 'EXPIRED' | 'NO_PAYMENT' | 'WAITING_START_DATE';
  message: string;
  nextBillingDate?: string;
  daysRemaining?: number;
  startDate?: string;
  subscriptionMonthsPaid?: number;
}

export interface RenewSubscriptionResponse {
  success: boolean;
  redirectUrl: string;
  totalAmount?: number;
  currency?: string;
  message?: string;
}

export interface RenewSubscriptionPayload {
  courseId: string;
  months?: number;
  currency?: string; // Added optional currency
}

export interface GetMyPaymentsResponse {
  success: boolean;
  data: Payment[];
}

export interface VerifyPaymentResponse {
  success: boolean;
  message: string;
}

export interface PendingPayment {
  id: string;
  amount: number;
  currency: string;
  createdAt: string;
  expiresAt: string;
  course?: {
    id: string;
    title: string;
    name: string;
  };
}

export interface PendingPaymentStatusResponse {
  success: boolean;
  hasPending: boolean;
  recentPending: PendingPayment[];
  stalePendingCount: number;
  message: string | null;
}

export interface ClearPendingResponse {
  success: boolean;
  cleared: number;
  message: string;
  clearedPayments?: Array<{
    id: string;
    amount: number;
    currency: string;
    clearedAt: string;
  }>;
}
// ============================================================
// PAYMENT API
// ============================================================

export const paymentAPI = {
  /**
   * Get enrollments for payment with optional currency
   * GET /payments/enrollments?currency={currencyCode}
   */
  getPaymentEnrollments: async (currency?: string): Promise<PaymentSummaryResponse> => {
    const query = currency ? `?currency=${currency}` : '';
    const response = await fetchWithAuth<PaymentSummaryResponse>(`/payments/enrollments${query}`);
    return response;
  },

  /**
   * Initiate payment with course selections and optional currency
   * POST /payments/initiate
   */
  initiatePayment: async (payload: PaymentInitiatePayload): Promise<PaymentInitiateResponse> => {
    const response = await fetchWithAuth<PaymentInitiateResponse>('/payments/initiate', {
      method: 'POST',
      body: JSON.stringify({
        courseSelections: payload.courseSelections,
        currency: payload.currency || 'KES',
      }),
    });
    return response;
  },

  /**
   * Get payment status for dashboard with optional currency
   * GET /payments/status?currency={currencyCode}
   */
  getPaymentStatus: async (currency?: string): Promise<PaymentStatusResponse> => {
    const query = currency ? `?currency=${currency}` : '';
    const response = await fetchWithAuth<PaymentStatusResponse>(`/payments/status${query}`);
    return response;
  },

  /**
   * Check access for a specific course
   * GET /payments/access/:courseId
   */
  checkCourseAccess: async (courseId: string): Promise<CheckAccessResponse> => {
    const response = await fetchWithAuth<CheckAccessResponse>(`/payments/access/${courseId}`);
    return response;
  },

  /**
   * Renew subscription for a course with optional currency
   * POST /payments/renew
   */
  renewSubscription: async (payload: RenewSubscriptionPayload): Promise<RenewSubscriptionResponse> => {
    const response = await fetchWithAuth<RenewSubscriptionResponse>('/payments/renew', {
      method: 'POST',
      body: JSON.stringify({
        courseId: payload.courseId,
        months: payload.months || 1,
        currency: payload.currency,
      }),
    });
    return response;
  },

  /**
   * Get my payment history
   * GET /payments/my-payments
   */
  getMyPayments: async (): Promise<Payment[]> => {
    const res = await fetchWithAuth<GetMyPaymentsResponse>('/payments/my-payments');
    return res.data;
  },

  /**
   * Verify payment after callback
   * POST /payments/verify
   */
  verifyPayment: async (orderTrackingId: string): Promise<VerifyPaymentResponse> => {
    const response = await fetchWithAuth<VerifyPaymentResponse>('/payments/verify', {
      method: 'POST',
      body: JSON.stringify({ orderTrackingId }),
    });
    return response;
  },

  /**
   * Get available currencies
   * GET /payments/currencies
   */
  getAvailableCurrencies: async (): Promise<SupportedCurrency[]> => {
    try {
      // Try to fetch from an endpoint first
      const response = await fetchWithAuth<{ currencies: SupportedCurrency[] }>('/payments/currencies');
      return response.currencies;
    } catch (error) {
      // Fallback: return empty array, component will use config from getPaymentStatus
      console.warn('Could not fetch currencies from endpoint, will use config from payment status');
      return [];
    }
  },
  
  /**
   * Get pending payment status
   * GET /payments/pending
   */
  getPendingPayments: async (): Promise<PendingPaymentStatusResponse> => {
    const response = await fetchWithAuth<PendingPaymentStatusResponse>('/payments/pending');
    return response;
  },

  /**
   * Clear stale pending payments
   * DELETE /payments/pending/clear?minutes={minutes}
   * @param minutes - Optional staleness threshold in minutes (default: 30)
   */
  clearPendingPayments: async (minutes?: number): Promise<ClearPendingResponse> => {
    const query = minutes ? `?minutes=${minutes}` : '';
    const response = await fetchWithAuth<ClearPendingResponse>(`/payments/pending/clear${query}`, {
      method: 'DELETE',
    });
    return response;
  },

  /**
   * Clear a specific pending payment by ID
   * DELETE /payments/pending/{paymentId}
   */
  clearSpecificPendingPayment: async (paymentId: string): Promise<{ success: boolean; message: string }> => {
    const response = await fetchWithAuth<{ success: boolean; message: string }>(`/payments/pending/${paymentId}`, {
      method: 'DELETE',
    });
    return response;
  },
};

